import type {
	PowerdayDailyReport,
	PriceMomentumReport,
	PriceStatistic,
	RMR,
	TrendIndicatorDailyReport,
} from "epsm-api-types/models";
import type { PriceInfo } from "epsm-api-types/ws";
import type { UTCTimestamp } from "lightweight-charts";
import type {
	IContext,
	PineJS,
	StudyMetaInfo,
} from "@/charting_library/charting_library";
import { chartColors } from "@/components/widgets/Chart/const";
import { mergeSortedResponses } from "@/components/widgets/Chart/helpers/mergeSortedPriceResponses/mergeSortedResponses";
import { PowerdayService } from "@/components/widgets/Chart/services/PowerdaysService/PowerdayService";
import { PriceMomentumService } from "@/components/widgets/Chart/services/PriceMomentumService/PriceMomentumService";
import { PriceStatisticsService } from "@/components/widgets/Chart/services/PriceStatisticsService/PriceStatisticsService";
import { RMRService } from "@/components/widgets/Chart/services/RMRService/RMRService";
import { TIMEFRAME_MAP } from "../../../../services/PriceService/PriceService";
import { TrendIndicatorService } from "../../../../services/TrendIndicatorService/TrendIndicatorService";
import { Subcharts, type Timeframes } from "../../../../types";
import {
	CustomIndicatorService,
	getDefaultParams,
} from "../CustomIndicatorService";

export const getPowerdayTrendValue = (trend: PowerdayDailyReport["trend"]) => {
	if (trend === "POSITIVE") return 1;
	if (trend === "NEGATIVE") return -1;
	return 0;
};

const getTrendValue = (trend: number | undefined) => {
	if (trend && trend > 0) return 1;
	if (trend && trend < 0) return -1;
	return 0;
};

const getEMATrendValue = (
	ema20: number | undefined,
	ema50: number | undefined,
) => {
	if (ema20 === undefined || ema50 === undefined) return -1;
	if (ema20 > ema50) return 2;
	if (ema20 < ema50) return 0;
	return 1;
};

const missingValueColorIndex = 3;

const checkValue = (value: number, valueToDisplay: number): number => {
	return Number.isFinite(value) ? valueToDisplay : missingValueColorIndex;
};

export class TimingMomentumIndicatorService extends CustomIndicatorService<TrendIndicatorDailyReport> {
	fieldMapper(item: TrendIndicatorDailyReport): PriceInfo<UTCTimestamp> {
		return {
			time: item.date.getTime(),
			close: item.trend_indicator?.trend_indicator_final,
		} as PriceInfo<UTCTimestamp>;
	}

	fieldMapperPowerday(item: PowerdayDailyReport): PriceInfo<UTCTimestamp> {
		const trendValue = getPowerdayTrendValue(item.trend);
		return {
			time: item.date.getTime(),
			open: trendValue,
		} as PriceInfo<UTCTimestamp>;
	}

	fieldMapperRMR(item: RMR): PriceInfo<UTCTimestamp> {
		return {
			time: item.date.getTime(),
			high: getTrendValue(item.composite_10d_slope),
		} as PriceInfo<UTCTimestamp>;
	}

	fieldMapperMomentum(item: PriceMomentumReport): PriceInfo<UTCTimestamp> {
		return {
			time: item.date.getTime(),
			low: getTrendValue(item.slope_price_momentum_value),
		} as PriceInfo<UTCTimestamp>;
	}

	fieldMapperEMA(item: PriceStatistic): PriceInfo<UTCTimestamp> {
		return {
			time: item.date.getTime(),
			volume: getEMATrendValue(item.ema?.d20, item.ema?.d50),
		} as PriceInfo<UTCTimestamp>;
	}

	async getBarData(
		from: number,
		to: number,
		interval: Timeframes,
		ticker: string,
	): Promise<PriceInfo<UTCTimestamp>[]> {
		if (TIMEFRAME_MAP[interval].isIntraday) {
			return [];
		}

		const [
			responseTrend,
			responsePowerday,
			responseRMR,
			responseMomentum,
			responseEMA,
		] = await Promise.all([
			TrendIndicatorService.create().getData(
				{
					...getDefaultParams(from, to, ticker),
					fields: ["date", "trend_indicator.trend_indicator_final"],
				},
				this.fieldMapper,
			),
			PowerdayService.create().getData(
				{ ...getDefaultParams(from, to, ticker), fields: ["date", "trend"] },
				this.fieldMapperPowerday,
			),
			RMRService.create().getData(
				{
					...getDefaultParams(from, to, ticker),
					fields: ["date", "composite_10d_slope"],
				},
				this.fieldMapperRMR,
			),
			PriceMomentumService.create().getData(
				{
					...getDefaultParams(from, to, ticker),
					fields: ["date", "slope_price_momentum_value"],
				},
				this.fieldMapperMomentum,
			),
			PriceStatisticsService.create().getData(
				{
					...getDefaultParams(from, to, ticker),
					fields: ["date", "ema.d20", "ema.d50"],
				},
				this.fieldMapperEMA,
			),
		]);

		const mergedResponse = mergeSortedResponses(
			responseTrend.data ?? [],
			responsePowerday.data ?? [],
			responseRMR.data ?? [],
			responseMomentum.data ?? [],
			responseEMA.data ?? [],
		);

		return mergedResponse as PriceInfo<UTCTimestamp>[];
	}

	getPlotsData(PineJS: PineJS, context: IContext): number[] {
		const trendIndicator = PineJS.Std.close(context);
		const powerdayIndicator = PineJS.Std.open(context);
		const rmrIndicator = PineJS.Std.high(context);
		const momentumIndicator = PineJS.Std.low(context);
		const ema = PineJS.Std.volume(context);

		return [
			3,
			2,
			1.5,
			1,
			0.5,
			-0.5,
			checkValue(trendIndicator, trendIndicator + 1),
			checkValue(momentumIndicator, momentumIndicator + 1),
			checkValue(rmrIndicator, rmrIndicator + 1),
			checkValue(powerdayIndicator, powerdayIndicator + 1),
			checkValue(ema, ema),
		];
	}

	get styles() {
		return {
			plot_0: {
				title: "Visual space up",
				histogramBase: 0,
			},
			plot_2: {
				title: "Trend",
				histogramBase: 0,
			},
			plot_3: {
				title: Subcharts.PriceMomSlope,
				histogramBase: 0,
			},
			plot_4: {
				title: "RMR 10Day Slope",
				histogramBase: 0,
			},
			plot_5: {
				title: Subcharts.PwdTrend,
				histogramBase: 0,
			},
			plot_10: {
				title: "EMA 20/50",
				histogramBase: 0,
			},
		};
	}

	get plots(): StudyMetaInfo["plots"] {
		return [
			{
				id: "plot_0",
				type: "line",
			},
			{
				id: "plot_2",
				type: "line",
			},
			{
				id: "plot_3",
				type: "line",
			},
			{
				id: "plot_4",
				type: "line",
			},
			{
				id: "plot_5",
				type: "line",
			},
			{
				id: "plot_10",
				type: "line",
			},
			{
				id: "plot_6",
				type: "colorer",
				target: "plot_2",
				palette: "paletteId1",
			},
			{
				id: "plot_7",
				type: "colorer",
				target: "plot_3",
				palette: "paletteId1",
			},
			{
				id: "plot_8",
				type: "colorer",
				target: "plot_4",
				palette: "paletteId1",
			},
			{
				id: "plot_9",
				type: "colorer",
				target: "plot_5",
				palette: "paletteId1",
			},
			{
				id: "plot_11",
				type: "colorer",
				target: "plot_10",
				palette: "paletteId1",
			},
		] as StudyMetaInfo["plots"];
	}

	get defaults(): StudyMetaInfo["defaults"] {
		return {
			styles: {
				plot_0: {
					plottype: 4,
					linestyle: 0,
					linewidth: 5,
					visible: true,
					transparency: 0,
					color: "black",
				},
				plot_2: {
					plottype: 4,
					linewidth: 5,
					linestyle: 0,
					visible: true,
					transparency: 0,
				},
				plot_3: {
					plottype: 4,
					linewidth: 5,
					linestyle: 0,
					visible: true,
					transparency: 0,
				},
				plot_4: {
					plottype: 4,
					linestyle: 0,
					linewidth: 5,
					visible: true,
					transparency: 0,
				},
				plot_5: {
					plottype: 4,
					linewidth: 5,
					linestyle: 0,
					visible: true,
					transparency: 0,
				},
				plot_10: {
					plottype: 4,
					linewidth: 5,
					linestyle: 0,
					visible: true,
					transparency: 0,
				},
			},
			palettes: {
				paletteId1: {
					colors: {
						0: {
							color: chartColors.down,
							width: 1,
							style: 0,
						},
						1: {
							color: chartColors.consolidation,
							width: 1,
							style: 0,
						},
						2: {
							color: chartColors.up,
							width: 1,
							style: 0,
						},
						3: {
							color: "black",
							width: 1,
							style: 0,
						},
					},
				},
			},
		};
	}

	get palettes(): StudyMetaInfo["palettes"] {
		return {
			paletteId1: {
				colors: {
					0: {
						name: "Down color",
					},
					1: {
						name: "Consolidation color",
					},
					2: {
						name: "Up color",
					},
					3: {
						name: "Missing",
					},
				},
			},
		};
	}
}
